import { create } from "zustand";
import { Platform, TurboModuleRegistry, DeviceEventEmitter } from "react-native";
import type { Spec } from "../../specs/NativeNetwork";
import { useSocketStore } from "./socketStore";

const NetworkModule: Spec | null = Platform.OS !== "web"
  ? TurboModuleRegistry.get<Spec>("NetworkModule")
  : null;

/** Grace period so brief drops that reconnect don't flash the offline toast. */
const SOCKET_DOWN_GRACE_MS = 3000;

interface NetworkState {
  isOffline: boolean;
  reason: "device" | "server" | null;
  setOffline: (offline: boolean, reason?: "device" | "server") => void;
  /** Debounced offline signal from the socket layer; superseded by setOffline. */
  reportSocketDown: () => void;
}

let socketDownTimer: ReturnType<typeof setTimeout> | null = null;
const clearSocketDownTimer = () => {
  if (socketDownTimer) {
    clearTimeout(socketDownTimer);
    socketDownTimer = null;
  }
};

/** Cancel a pending offline toast — for intentional disconnects whose reason is shown elsewhere. */
export function cancelSocketDown(): void {
  clearSocketDownTimer();
}

/** Whether the device has a network, to distinguish a "device" offline from a
 *  "server" one. Assumes online if it can't tell. */
export async function isDeviceOnline(): Promise<boolean> {
  if (Platform.OS === "web") {
    return typeof navigator === "undefined" || navigator.onLine;
  }
  if (NetworkModule) {
    try {
      return await NetworkModule.isConnected();
    } catch {
      return true;
    }
  }
  return true;
}

export const useNetworkStore = create<NetworkState>((set, get) => ({
  isOffline: false,
  reason: null,
  setOffline: (offline, reason = null) => {
    clearSocketDownTimer();
    const wasOffline = get().isOffline;
    set({ isOffline: offline, reason: offline ? reason : null });

    // When transitioning from offline → online, reconnect the socket if it's dead
    if (wasOffline && !offline) {
      const { socket, isConnected } = useSocketStore.getState();
      if (socket && !isConnected) {
        socket.connect();
      } else if (!socket) {
        useSocketStore.getState().initSocket().catch(console.error);
      }
    }
  },
  reportSocketDown: () => {
    // Don't re-arm on repeated errors, or the toast never lands.
    if (get().isOffline || socketDownTimer) return;
    socketDownTimer = setTimeout(async () => {
      socketDownTimer = null;
      if (useSocketStore.getState().isConnected) return;
      // Probe connectivity directly — a native device-offline event can lag the socket drop.
      const online = await isDeviceOnline();
      if (get().isOffline || useSocketStore.getState().isConnected) return;
      get().setOffline(true, online ? "server" : "device");
    }, SOCKET_DOWN_GRACE_MS);
  },
}));

/**
 * Initialize device-level network listeners.
 * Call once at app startup.
 */
if (typeof __DEV__ !== "undefined" && __DEV__ && Platform.OS === "web" && typeof window !== "undefined") {
  (window as any).useNetworkStore = useNetworkStore;
}

let initialized = false;

export function initNetworkListeners() {
  if (initialized) return;
  initialized = true;

  if (Platform.OS === "web") {
    const update = () => {
      const offline = !navigator.onLine;
      useNetworkStore.getState().setOffline(offline, offline ? "device" : undefined);
    };
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    update();
  } else if (NetworkModule) {
    // Native: use NetworkModule (ConnectivityManager)
    NetworkModule.isConnected().then((connected: boolean) => {
      if (!connected) {
        useNetworkStore.getState().setOffline(true, "device");
      }
    });

    // Native emits on the global device-event channel (RCTDeviceEventEmitter),
    // so listen via DeviceEventEmitter rather than wrapping the module in a
    // NativeEventEmitter (which expects the iOS RCTEventEmitter add/remove API).
    DeviceEventEmitter.addListener("networkStatusChange", (connected: boolean) => {
      if (connected) {
        useNetworkStore.getState().setOffline(false);
      } else {
        useNetworkStore.getState().setOffline(true, "device");
      }
    });
    NetworkModule.startListening();
  }
}
